import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { Bell, Check, CheckCheck, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { formatDateMDY } from '../lib/date';

const typeIcons = {
  task_assigned: '📋',
  task_updated: '🔄',
  comment_added: '💬',
  mentioned: '💬',
  task_completed: '✅',
  status_changed: '📊',
  info: '💡',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return formatDateMDY(date);
}

export const NotificationsPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications?limit=30');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silently fail
    }
  }, []);

  // Fetch on mount and poll every 30s
  useEffect(() => {
    fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchNotifications]);

  // Refetch when popover opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const markAllRead = async () => {
    try {
      await api.post('/api/notifications/mark-read');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // silently fail
    }
  };

  const handleClick = async (notification) => {
    // Mark this one as read
    if (!notification.read) {
      try {
        await api.post(`/api/notifications/${notification.id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // silently fail
      }
    }

    // Determine target URL: use href, or construct from entity_type/entity_id
    let target = notification.href;
    if (!target && notification.entity_type === 'task' && notification.entity_id) {
      target = `/tasks?open=${notification.entity_id}`;
    }

    if (target) {
      setOpen(false);
      navigate(target);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative flex items-center justify-center w-9 h-9 rounded-lg text-[#676879] dark:text-[#9CA0AF] hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30] hover:text-[#323338] dark:hover:text-white"
          style={{ transition: 'background-color 0.15s ease, color 0.15s ease' }}
          data-testid="notifications-bell"
        >
          <Bell className="w-4.5 h-4.5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white bg-[#E2445C] rounded-full px-1"
              data-testid="notifications-unread-count"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-80 sm:w-96 p-0 border-[#E6E9EF] dark:border-[#3a3b40] dark:bg-[#1e1f23]"
        align="end"
        sideOffset={8}
        data-testid="notifications-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E9EF] dark:border-[#3a3b40]">
          <h3 className="text-sm font-semibold text-[#323338] dark:text-white" style={{ fontFamily: 'var(--font-heading)' }}>
            Notifications
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-[#0073EA] hover:text-[#0060B9] hover:bg-[#0073EA]/10"
              onClick={markAllRead}
              data-testid="notifications-mark-all-read"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* List */}
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[#676879] dark:text-[#9CA0AF]">
              <Bell className="w-8 h-8 mb-2 text-[#C3C6D4] dark:text-[#4a4b50]" />
              <p className="text-sm font-medium">No notifications yet</p>
              <p className="text-xs text-[#9CA0AF] mt-0.5">We'll notify you when something happens</p>
            </div>
          ) : (
            <div className="py-1">
              {notifications.map((n) => (
                <button
                  key={n.id}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30] cursor-pointer group ${
                    !n.read ? 'bg-[#0073EA]/[0.03]' : ''
                  }`}
                  style={{ transition: 'background-color 0.15s ease' }}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n.id}`}
                >
                  <span className="text-base flex-shrink-0 mt-0.5">
                    {typeIcons[n.type] || typeIcons.info}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!n.read ? 'font-semibold text-[#323338] dark:text-white' : 'text-[#676879] dark:text-[#9CA0AF]'}`}>
                      {n.title}
                    </p>
                    {n.message && n.message !== n.title && (
                      <p className="text-xs text-[#676879] dark:text-[#9CA0AF] mt-0.5 line-clamp-2">{n.message}</p>
                    )}
                    <p className="text-[10px] text-[#9CA0AF] mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && (
                    <span className="w-2 h-2 rounded-full bg-[#0073EA] flex-shrink-0 mt-2" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsPanel;
